'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { captureError, flushErrorsSync, startAutoFlush, stopAutoFlush } from '@/lib/errorReporter';
import type { ErrorContext } from '@/lib/errorReporter';
interface ErrorReporterProps {
  children: React.ReactNode;
}

/**
 * ErrorReporter — global error capture boundary wired into the root layout.
 *
 * Responsibilities:
 * 1. Registers window.onerror and window.onunhandledrejection to catch non-React errors
 * 2. Registers beforeunload handler to sendBeacon remaining buffered errors
 * 3. Starts/stops the periodic auto-flush timer on mount/unmount
 * 4. Wraps children in ErrorBoundary with onError wired to captureError
 *
 * Observability surfaces:
 * - Network tab: POST /api/_obs/errors/batch calls visible in browser DevTools
 * - getBufferSize() export for programmatic buffer inspection
 * - _frontend_errors table in obs DB for stored errors
 */
export function ErrorReporter(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp4, t1);
  const handleReactError = _temp5;
  let t2;
  if ($[1] !== children) {
    t2 = <ErrorBoundary onError={handleReactError}>{children}</ErrorBoundary>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp5(error, info) {
  const context = {
    ...(info.componentStack ? {
      componentStack: info.componentStack
    } : {})
  };
  captureError(error, context);
}
function _temp4() {
  const handleWindowError = _temp;
  const handleUnhandledRejection = _temp2;
  const handleBeforeUnload = _temp3;
  window.addEventListener("error", handleWindowError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  window.addEventListener("beforeunload", handleBeforeUnload);
  startAutoFlush();
  return () => {
    window.removeEventListener("error", handleWindowError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    window.removeEventListener("beforeunload", handleBeforeUnload);
    stopAutoFlush();
  };
}
function _temp3() {
  flushErrorsSync();
}
function _temp2(event_0) {
  captureError(event_0.reason ?? "Unhandled promise rejection");
}
function _temp(event) {
  captureError(event.error ?? event.message);
}